// ============================================================================
// Parse a user's OWN material into flashcards so they can use the revision
// flow without AI generation. Nothing is uploaded or saved — parsing is local.
//
// Accepted formats (auto-detected):
//   1. Q/A blocks, blank-line separated:
//        Q: What is photosynthesis?
//        A: The process plants use to turn light into energy.
//
//        Q: ...
//        A: ...
//   2. One card per line with TAB separator:   front<TAB>back
//   3. One card per line as simple CSV:        front,back
//      (only used when a line has no Q:/A: and no tab; splits on the FIRST comma)
// ============================================================================

export interface ImportedCard {
  question: string;
  answer: string;
}

/** Strip a leading "Q:"/"A:" (also Chinese 問/答, Q：) label from a line. */
function stripLabel(line: string, label: 'q' | 'a'): string {
  const patterns =
    label === 'q'
      ? [/^\s*(?:q|question|問|问题)\s*[:：]\s*/i]
      : [/^\s*(?:a|answer|答|答案)\s*[:：]\s*/i];
  let out = line;
  for (const p of patterns) out = out.replace(p, '');
  return out.trim();
}

function parseQaBlocks(text: string): ImportedCard[] {
  const blocks = text.split(/\n\s*\n/);
  const cards: ImportedCard[] = [];
  for (const block of blocks) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;
    const qLines: string[] = [];
    const aLines: string[] = [];
    let mode: 'none' | 'q' | 'a' = 'none';
    for (const line of lines) {
      if (/^(?:q|question|問|问题)\s*[:：]/i.test(line)) {
        mode = 'q';
        qLines.push(stripLabel(line, 'q'));
      } else if (/^(?:a|answer|答|答案)\s*[:：]/i.test(line)) {
        mode = 'a';
        aLines.push(stripLabel(line, 'a'));
      } else if (mode === 'q') {
        qLines.push(line);
      } else if (mode === 'a') {
        aLines.push(line);
      }
    }
    const question = qLines.join(' ').trim();
    const answer = aLines.join(' ').trim();
    if (question && answer) cards.push({ question, answer });
  }
  return cards;
}

function parseDelimitedLines(text: string): ImportedCard[] {
  const cards: ImportedCard[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    let question = '';
    let answer = '';
    if (line.includes('\t')) {
      const idx = line.indexOf('\t');
      question = line.slice(0, idx).trim();
      answer = line.slice(idx + 1).trim();
    } else if (line.includes(',')) {
      const idx = line.indexOf(',');
      question = line.slice(0, idx).trim();
      answer = line.slice(idx + 1).trim();
    }
    // Strip surrounding quotes from CSV cells.
    const unquote = (s: string) => s.replace(/^["']|["']$/g, '').trim();
    question = unquote(question);
    answer = unquote(answer);
    if (question && answer) cards.push({ question, answer });
  }
  return cards;
}

/** Parse raw text into cards; chooses Q/A-block or delimited-line parsing. */
export function parseFlashcards(text: string): ImportedCard[] {
  if (!text || !text.trim()) return [];
  const looksLikeQa = /(?:^|\n)\s*(?:q|question|問|问题)\s*[:：]/i.test(text);
  const cards = looksLikeQa ? parseQaBlocks(text) : parseDelimitedLines(text);
  // De-duplicate by question+answer, preserving order.
  const seen = new Set<string>();
  return cards.filter((c) => {
    const key = `${c.question}||${c.answer}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Read a File (.txt/.csv) into text and parse it. */
export async function parseFlashcardFile(file: File): Promise<ImportedCard[]> {
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
  return parseFlashcards(text);
}

/** A starter template users can download to see the expected format. */
export const FLASHCARD_TEMPLATE = `Q: What is photosynthesis?
A: The process by which green plants use sunlight to make food from carbon dioxide and water.

Q: What is the powerhouse of the cell?
A: The mitochondrion.
`;
